import { defineStore } from 'pinia'
import th from '@tillhub/javascript-sdk'
import { useAuthStore } from '@shared/store/auth'
import {
  getConnectedDashboards,
  getActiveClientAccountId,
  getClientAccountId,
  getUnzerId
} from '@shared/plugins/auth'
import { applyTenantContextEffects } from './tenant-context-effects'

export const useConnectedDashboardsStore = defineStore('connectedDashboards', {
  persist: {
    pick: ['activeUnzerId', 'primaryName']
  },
  state: () => ({
    dashboards: [],
    connections: [],
    parentConnections: [],
    activeUnzerId: null,
    primaryUnzerId: null,
    primaryName: null
  }),

  getters: {
    connectedDashboards: (state) => state.dashboards,
    allConnections: (state) => state.connections,
    currentDashboard: (state) =>
      state.dashboards.find((d) => d.unzerId === state.activeUnzerId) || null,
    hasMultipleDashboards: (state) => state.dashboards.length > 1,
    onPrimaryDashboard: (state) =>
      !state.primaryUnzerId || state.activeUnzerId === state.primaryUnzerId
  },

  actions: {
    async fetchConnections({ useActiveTenant = false } = {}) {
      const clientAccountId = useActiveTenant
        ? getActiveClientAccountId()
        : getClientAccountId()
      if (!clientAccountId) {
        this.connections = []
        this.parentConnections = []
        return []
      }

      try {
        const { data } = await th
          .clientAccounts()
          .getConnections(clientAccountId)
        const {
          connectedDashboardsList = [],
          connectedDashboards = {},
          connectedDashboardsDetails = {},
          parentConnectedDashboardsDetails = {}
        } = data || {}
        this.connections = connectedDashboardsList.map((unzerId) => ({
          unzerId,
          name: connectedDashboardsDetails[unzerId]?.name || unzerId,
          clientAccountId:
            connectedDashboardsDetails[unzerId]?.clientAccountId || null,
          roles: connectedDashboards[unzerId] || []
        }))
        this.parentConnections = Object.entries(
          parentConnectedDashboardsDetails
        ).map(([unzerId, detail]) => ({
          unzerId,
          name: detail?.name || unzerId,
          clientAccountId: detail?.clientAccountId || null
        }))
        return this.connections
      } catch (error) {
        this.$logException?.(error, { trackError: false })
        this.connections = []
        this.parentConnections = []
        return []
      }
    },

    async refreshConnectionsForActiveTenant() {
      this.connections = []
      this.parentConnections = []
      return this.fetchConnections({ useActiveTenant: true })
    },

    async loadConnectedDashboards() {
      const clientAccountId = getClientAccountId()
      if (!clientAccountId) {
        this.dashboards = []
        this.connections = []
        this.parentConnections = []
        this.activeUnzerId = null
        this.primaryUnzerId = null
        this.primaryName = null
        return
      }

      const fromToken = getConnectedDashboards()
      if (!fromToken) {
        this.dashboards = []
        this.activeUnzerId = null
        this.primaryUnzerId = null
        return
      }

      const tokenMap = fromToken.map
      const allowedUnzerIds = new Set(Object.keys(tokenMap))
      const ownUnzerId =
        getUnzerId() || fromToken.activeUnzerId || clientAccountId || null

      await this.fetchConnections({ useActiveTenant: false })

      const filteredConnections = this.connections.filter(
        (connection) =>
          allowedUnzerIds.has(connection.unzerId) &&
          connection.unzerId !== ownUnzerId
      )

      const detailByUnzerId = filteredConnections.reduce((acc, connection) => {
        acc[connection.unzerId] = connection
        return acc
      }, {})

      const childIds = filteredConnections.map(
        (connection) => connection.unzerId
      )

      this.applyDashboards(ownUnzerId, childIds, tokenMap, detailByUnzerId)
    },

    applyDashboards(ownUnzerId, childIds, tokenMap, detailByUnzerId = {}) {
      if (!childIds.length) {
        this.dashboards = []
        this.activeUnzerId = null
        this.primaryUnzerId = null
        return
      }

      const auth = useAuthStore()

      const ownAccountId = getClientAccountId()
      const activeAccountId =
        globalThis.localStorage.getItem('clientAccountId') || ownAccountId
      if (
        activeAccountId === ownAccountId &&
        (auth.accountName || auth.orgName)
      ) {
        this.primaryName = auth.accountName || auth.orgName
      }

      const ownDashboard = ownUnzerId
        ? [
            {
              unzerId: ownUnzerId,
              roles: tokenMap[ownUnzerId] || [],
              name: this.primaryName || ownUnzerId,
              clientAccountId: ownAccountId || null
            }
          ]
        : []

      const connectedDashboards = childIds.map((unzerId) => {
        const detail = detailByUnzerId[unzerId]
        return {
          unzerId,
          roles: tokenMap[unzerId] || [],
          name: detail?.name || unzerId,
          clientAccountId: detail?.clientAccountId || null
        }
      })

      this.dashboards = [...ownDashboard, ...connectedDashboards]

      this.primaryUnzerId = ownUnzerId

      const persistedIsValid =
        this.activeUnzerId &&
        this.dashboards.some((d) => d.unzerId === this.activeUnzerId)
      if (!persistedIsValid) {
        this.activeUnzerId = ownUnzerId || this.dashboards[0]?.unzerId || null
      }
    },

    async switchDashboard(unzerId) {
      const target = this.dashboards.find((d) => d.unzerId === unzerId)
      if (!target || unzerId === this.activeUnzerId) return null

      const isPrimary = unzerId === this.primaryUnzerId
      if (!isPrimary && !target.clientAccountId) {
        this.$logException?.(
          new Error(
            `Cannot switch to dashboard ${unzerId}: connections response has no clientAccountId`
          ),
          { trackError: false }
        )
        return null
      }

      await useAuthStore().reScopeToClientAccount(
        isPrimary ? null : target.clientAccountId
      )
      this.activeUnzerId = unzerId

      return target
    },

    async completeDashboardSwitch() {
      await applyTenantContextEffects()
      await this.refreshConnectionsForActiveTenant()
    }
  }
})
